<template>
  <v-card class="trades-table">
    <v-card-title class="toolbar white--text">
      <h2>Trades ({{ filteredItems.length }} of {{ pagination.totalItems }} )</h2>
      <v-spacer />
      <v-select
        v-model="search"
        primary
        dark
        solo-inverted
        flat
        append-icon="pending_actions"
        class="ma-0 mr-4"
        :items="tradeStates"
        label="Trade State"
        item-value="value"
        hide-details
        clearable
      />
      <v-text-field
        v-model="search"
        primary
        dark
        solo-inverted
        flat
        append-icon="search"
        label="Search trades by user, escrow, security wallet..."
        single-line
        hide-details
      />
    </v-card-title>
    <v-card-title class="toolbar white--text">
      <v-spacer />
      <v-flex
        xs12
        sm6
        md4
      >
        <v-menu
          v-model="pickerStartDate"
          :close-on-content-click="false"
          :nudge-right="40"
          lazy
          transition="scale-transition"
          offset-y
          full-width
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-model="startDate"
              label="Filter Starting Date"
              append-icon="event"
              readonly
              clearable
              box
              dark
              flat
              icon
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="startDate"
            locale="en-UK"
            @input="pickerStartDate = false"
          />
        </v-menu>
      </v-flex>
      <v-flex
        xs12
        sm6
        md4
        ml-4
      >
        <v-menu
          v-model="pickerEndDate"
          :nudge-right="40"
          lazy
          transition="scale-transition"
          offset-y
          full-width
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-model="endDate"
              label="Filter Ending Date"
              append-icon="event"
              readonly
              clearable
              box
              dark
              flat
              icon
              primary
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="endDate"
            locale="en-UK"
            @input="pickerEndDate = false"
          />
        </v-menu>
      </v-flex>
    </v-card-title>

    <v-card-title class="filters grey lighten-5">
      <div class="filters__header">
        Filtered results ({{ filteredResults.length }} of {{ filteredItems.length }})
        <v-tooltip
          v-if="!hasResultFilters"
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              dark
              flat
              icon
              color="secondary"
              v-on="on"
            >
              <v-icon>
                filter_list
              </v-icon>
            </v-btn>
          </template>
          <span>Filters</span>
        </v-tooltip>
        <v-tooltip
          v-else
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              dark
              flat
              icon
              color="primary"
              v-on="on"
              @click="resetResultsFilters"
            >
              <v-icon>
                business
              </v-icon>
            </v-btn>
          </template>
          <span>Reset filters</span>
        </v-tooltip>
      </div>
      <v-select
        v-model="resultFilters.providerType"

        class="ml-5"
        primary
        :items="providers"
        label="Provider"
        item-value="value"
        clearable
      />

      <v-select
        v-model="resultFilters.assetType"
        class="ml-5"
        primary
        :items="assetTypes"
        label="Type"
        item-value="value"
        clearable
      />

      <v-text-field
        v-model="resultFilters.searchResults"

        class="ml-5"
        append-icon="search"
        label="filter results"
      />
    </v-card-title>

    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="filteredResults"
      :total-items.sync="tradesPagination.total"
      :rows-per-page-items="rowsPerPageItems"
      :loading="loading"
      :expand="expand"
      item-key="id"
      class="elevation-1"
      :pagination.sync="pagination"
      @update:pagination="updatePagination"
    >
      <template v-slot:items="props">
        <tr
          :active="props.selected"
          @click="props.expanded = !props.expanded"
        >
          <td class="text-xs-center">
            {{ props.item.id }}
          </td>
          <td class="text-xs-center">
            {{ props.item.buy_offer_id ? 'Buy Offer' : 'Sell Offer' }}
          </td>
          <td class="text-xs-center">
            {{ props.item.sell_offer_id || props.item.buy_offer_id }}
          </td>
          <td class="text-xs-center">
            {{ sellerId(props.item) }}
          </td>
          <td class="text-xs-center">
            {{ buyerId(props.item) }}
          </td>
          <td class="text-xs-center">
            {{ props.item.escrow_id }}
          </td>
          <td class="text-xs-center">
            {{ offer(props.item).asset.title }}
          </td>
          <td class="text-xs-center">
            {{ formatFieldName(offer(props.item).asset.provider) }}
          </td>
          <td class="text-xs-center">
            {{ formatFieldName(offer(props.item).asset.type) }}
          </td>
          <td class="text-xs-center">
            {{ formatFieldName(props.item.state) }}
          </td>
          <td class="text-xs-center">
            {{ props.item.pieces }}
          </td>
          <td class="text-xs-center">
            {{ centsToEuros(props.item.total) }}
          </td>
          <td class="text-xs-center">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-chip
                  color="grey"
                  outline
                  small
                  v-on="on"
                >
                  <span>
                    {{ formatDateSmall(props.item.created_at) }}
                  </span>
                </v-chip>
              </template>
              {{ formatDate(props.item.created_at) }}
            </v-tooltip>
          </td>
          <td class="text-xs-center">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-chip
                  color="grey"
                  outline
                  small
                  v-on="on"
                >
                  <span>
                    {{ formatDateSmall(props.item.updated_at) }}
                  </span>
                </v-chip>
              </template>
              {{ formatDate(props.item.updated_at) }}
            </v-tooltip>
          </td>
        </tr>
      </template>
      <template v-slot:expand="props">
        <TradeDetail
          :item="props.item"
        />
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import _ from 'lodash';
import { mapActions } from 'vuex';
import { toQueryString, setQuery } from '@/helpers';
import TradeStatesEnum from '@/enum/trades/TradeStates';
import AssetProvidersEnum from '@/enum/trades/AssetProviders';
import CustodyTypesEnum from '@/enum/trades/CustodyTypes';
import TradeDetail from '@/components/trades/shared/TradeDetail';

export default {
  name: 'TradesTable',
  components: {
    TradeDetail,
  },
  props: {
    trades: {
      type: Array,
      default: () => [],
    },
    tradesPagination: {
      type: Object,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expand: false,
      isLoadTableData: false,
      search: undefined,
      startDate: this.tradesPagination.startDate,
      endDate: this.tradesPagination.endDate,
      selected: [],
      pagination: {
        page: this.tradesPagination.page,
        rowsPerPage: this.tradesPagination.limit,
        sortBy: 'created_at',
        descending: true,
        startDate: this.tradesPagination.startDate,
        endDate: this.tradesPagination.endDate,
      },
      rowsPerPageItems: [
        {
          text: '10',
          value: 10,
        },
        {
          text: '25',
          value: 25,
        },
        {
          text: '50',
          value: 50,
        },
        {
          text: '100',
          value: 100,
        },
      ],
      filters: {
        createdAt: [],
        state: [],
      },
      resultFilters: {
        assetType: null,
        providerType: null,
        searchResults: null,
      },
      headers: [
        {
          text: 'Id',
          align: 'left',
          value: 'id',
        },
        {
          text: 'Offer Type',
          align: 'left',
          value: 'sell_offer_id',
        },
        {
          text: 'Offer ID',
          align: 'left',
          value: 'sell_offer_id',
        },
        {
          text: 'Seller Id',
          align: 'left',
          value: 'offer.user_id',
        },
        {
          text: 'Buyer Id',
          align: 'left',
          value: 'user_id',
        },
        {
          text: 'Escrow',
          align: 'left',
          value: 'escrow_id',
        },
        {
          text: 'Project',
          value: 'offer.asset.title',
          sortable: false,
        },
        {
          text: 'Provider',
          value: 'offer.asset.provider',
          sortable: false,
        },
        {
          text: 'Type',
          value: 'offer.asset.type',
          sortable: false,
        },
        {
          text: 'State',
          align: 'left',
          value: 'state',
        },
        {
          text: 'Pieces',
          align: 'left',
          value: 'pieces',
        },
        {
          text: 'Total',
          align: 'left',
          value: 'total',
        },
        {
          text: 'Created at',
          value: 'created_at',
        },
        {
          text: 'Updated at',
          value: 'updated_at',
        },
      ],
      pickerStartDate: null,
      pickerEndDate: null,
    };
  },
  computed: {
    filteredItems() {
      return this.trades.filter(d => Object.keys(this.filters).every((f) => {
        if (this.filters[f]) {
          return (
            this.filters[f].length < 1 ||
            String(this.filters[f]).includes(d[f])
          );
        }

        return true;
      }));
    },
    filteredResults() {
      if (this.hasResultFilters) {
        return this.filteredItems.filter((item) => {
          let hasProvider = false;
          let hasAssetType = false;
          let hasSearch = false;

          const { providerType, assetType, searchResults } = this.resultFilters;

          if (providerType) {
            hasProvider = this.offer(item).asset.provider === providerType;
          }
          if (assetType) {
            hasAssetType = this.offer(item).asset.type === assetType;
          }

          if (searchResults) {
            hasSearch = JSON.stringify(item)
              .toLowerCase()
              .includes(searchResults.toLowerCase());
          }

          return (!!hasProvider || !!hasAssetType || !!hasSearch) || false;
        });
      }

      const { sortBy, descending } = this.pagination;

      if (sortBy && sortBy.includes('user_id')) {
        const items = [...this.filteredItems];

        items.sort((a, b) => {
          if (sortBy === 'user_id') {
            return Number(a.user_id) - Number(b.user_id);
          }

          return Number(this.offer(a).user_id) - Number(this.offer(b).user_id);
        });

        return descending ? items : items.reverse();
      }

      return this.filteredItems;
    },

    tradeStates() {
      return Object.keys(TradeStatesEnum).map(key => ({
        text: this.formatFieldName(TradeStatesEnum[key]),
        value: TradeStatesEnum[key],
      }));
    },
    providers() {
      return Object.keys(AssetProvidersEnum).map(key => ({
        text: this.formatFieldName(AssetProvidersEnum[key]),
        value: AssetProvidersEnum[key],

      }));
    },
    assetTypes() {
      return Object.keys(CustodyTypesEnum).map(key => ({
        text: this.formatFieldName(CustodyTypesEnum[key]),
        value: CustodyTypesEnum[key],
      }));
    },
    hasResultFilters() {
      return !!this.resultFilters.providerType
        || !!this.resultFilters.assetType
        || !!this.resultFilters.searchResults;
    },
  },
  watch: {
    async trades() {
      this.getDefaultFilters();
      await this.$nextTick();
      await this.changePage();
    },
    tradesPagination: {
      immediate: false,
      async handler(tradesPagination) {
        if (tradesPagination) {
          this.pagination.page = tradesPagination.page;
          this.pagination.rowsPerPage = tradesPagination.limit;
          this.pagination.totalItems = tradesPagination.total;
          this.pagination.startDate = tradesPagination.startDate;
          this.pagination.endDate = tradesPagination.endDate;
        }
      },
    },
    search: {
      immediate: false,
      handler(search, oldSearch) {
        if (search !== oldSearch) {
          setQuery(
            {
              search,
            },
            this.$route,
            this.$router,
          );
          this.refreshTable();
        }
      },
    },
    startDate: {
      handler(startDate) {
        startDate += ' 00:00:00';
        setQuery(
          {
            startDate,
          },
          this.$route,
          this.$router,
        );
        this.pagination.startDate = startDate;
        this.refreshTable();
      },
    },
    endDate: {
      handler(endDate) {
        endDate += ' 00:00:00';
        setQuery(
          {
            endDate,
          },
          this.$route,
          this.$router,
        );
        this.pagination.endDate = endDate;
        this.refreshTable();
      },
    },
    pagination: {
      immediate: false,
      handler(pagination, oldPagination) {
        if (!_.isEqual({
          descending: pagination.descending,
          page: pagination.page,
          rowsPerPage: pagination.rowsPerPage,
          sortBy: pagination.sortBy,
          totalItems: pagination.totalItems,
          startDate: pagination.startDate,
          endDate: pagination.endDate,
        }, {
          descending: oldPagination.descending,
          page: oldPagination.page,
          rowsPerPage: oldPagination.rowsPerPage,
          sortBy: oldPagination.sortBy,
          totalItems: oldPagination.totalItems || 0,
          startDate: oldPagination.startDate,
          endDate: oldPagination.endDate,
        })) {
          // if (pagination.sortBy && !pagination.sortBy.includes('user_id')) {
          this.refreshTable();
          // }
        }
      },
    },
  },
  methods: {
    ...mapActions(['getTrades']),
    refreshTable() {
      const query = toQueryString({
        page: this.pagination.page || 1,
        limit: this.pagination.rowsPerPage || 25,
        search: this.search,
        sort: this.pagination.sortBy,
        dir: this.pagination.descending ? 'desc' : 'asc',
        startDate: this.startDate,
        endDate: this.endDate,
      });
      if (typeof window.LIT !== 'undefined') {
        clearTimeout(window.LIT);
      }
      window.LIT = setTimeout(() => {
        this.getTrades(query);
      }, 1000);
    },
    formatDate(date) {
      return this.$moment(date).format('MMMM Do YYYY, h:mm:ss a');
    },
    formatDateSmall(date) {
      return this.$moment(date).format('DD/MM/YYYY');
    },
    centsToEuros(amount) {
      amount = `${parseInt(amount, 0) / 100}`;

      return `${parseFloat(amount).toFixed(2)} €`;
    },
    offer(item) {
      return item.buy_offer_id
        ? item.buy_offer
        : item.sell_offer;
    },
    sellerId(item) {
      return item.buy_offer_id
        ? item.user_id
        : this.offer(item).user_id;
    },
    buyerId(item) {
      return item.buy_offer_id
        ? this.offer(item).user_id
        : item.user_id;
    },
    /**
     * removes keys from object if value is falsy or typeof object
     */
    cleanFields(data) {
      return Object.keys(data)
        .filter(field => !!data[field] && typeof data[field] !== 'object');
    },
    updatePagination(pagination) {
      setQuery(
        {
          page: pagination.page,
          sortBy: pagination.sortBy,
          dir: pagination.descending ? 'desc' : 'asc',
          search: this.search,
          startDate: pagination.startDate,
          endDate: pagination.endDate,
        },
        this.$route,
        this.$router,
      );
    },
    toogleSelected(props) {
      props.selected = !props.selected;
    },
    async changePage() {
      const { page, search } = this.$route.query;
      this.search = search;
      await this.$nextTick();
      this.isLoadTableData = true;
      this.pagination.page = page ? Number(page) : 1;
    },
    getDefaultFilters() {
      if (this.$route.query) {
        Object.keys(this.$route.query).forEach((item) => {
          let value = this.$route.query[item];

          if (Object.prototype.hasOwnProperty.call(this.filters, item)) {
            if (typeof this.filters[item] === 'string') {
              this.filters[item] = value;
            } else if (String(this.$route.query[item]).includes(';')) {
              value = this.$route.query[item].split(';');
              this.filters[item] = value;
            } else {
              this.filters[item].push(value);
            }
          }
        });
      }
    },
    formatFieldName(str, item) {
      const camelCaseSentence = str.replace(/([-_]\w)/g, g => g[1].toUpperCase());
      const sentence = _.startCase(camelCaseSentence);

      if (item) {
        if (str === 'security_wallet' && this.offer(item).asset.provider === 'baaderbank') {
          return `${_.capitalize(sentence)} / Baader Bank account`;
        }
      }

      return _.capitalize(sentence);
    },

    formatValue(value, key) {
      if (key === 'total' || key === 'unit_price') {
        return this.centsToEuros(value);
      }
      if (key.includes('_at')) {
        return this.formatDate(value);
      }

      return value;
    },

    resetResultsFilters() {
      this.resultFilters = _.mapValues(this.resultFilters, () => null);
    },

    checkObjectKeys(item, value) {
      return JSON.stringify(item)
        .toLowerCase()
        .includes(value);
    },
  },
};
</script>

<style lang="scss" scoped>
.v-menu__content {
  min-width: auto !important;
}

.trades-table{
  a {
    text-decoration: none;
    color: black;
  }

  .toolbar {
    background-color: #1a4c7d;
    color: white;
  }

  .infoLabel {
    margin-left: 16px;
  }

  .primary--text {
    color: white !important;
  }

  .countLabel {
    color: #fff;
  }
  .expanded-details {
    background-color: #eaeaea;
    padding: 20px;
  }

  .v-select{
    flex: 1;
  }

  table.v-table {
    td:not(:first-child),
    th:not(:first-child) {
      padding: 0 1% !important;
    }
  }

  .v-card__title.subheading{
    .v-icon{
      margin-right:10px;
    }
  }
  .v-input{
    &.security_wallet{
      min-width: 350px;
    }
  }

}
</style>



// WEBPACK FOOTER //
// src/components/trades/TradesTable.vue